import * as React from "react";

// NPM
import clsx from "clsx";
import { animated } from "react-spring";
import NumberFormat from "react-number-format";

// MUI
import {
	withStyles,
	WithStyles,
	createStyles,
	Theme,
} from "@material-ui/core/styles";
import { ThemeProvider, Tooltip } from "@material-ui/core";

// Utility
import Utility from "../../domain/classes/Utility";

// Assets

// Utility
import medicareTheme from "../../domain/utility/medicareTheme";

// Contexts
import { useApplicationManager } from "../../contexts/ApplicationManager.Context";

// Classes
import Concession from "../../domain/classes/Concession";
import AuditableDocument from "../../domain/classes/AuditableDocument";

// Interfaces
import IConcessionCardError, {
	InitialConcessionCardErrorState,
} from "../../domain/interfaces/IConcessionCardError";

// Errors
import InvalidExpiryError from "../../domain/classes/errors/InvalidExpiryError";
import { BLANK_HEALTHCARE } from "../../domain/utility/config";

export const styles = (theme: Theme) =>
	createStyles({
		imgContainer: { width: "100%", overflow: "hidden" },
		img: { width: "100%" },
		root: {
			position: "relative",
			display: "block",
			borderRadius: theme.spacing(2),
			overflow: "hidden",
		},
		concessionCardNumberContainer: {
			position: "relative",
			display: "flex",
			width: 200,
		},
		concessionCardNumberWrapper: {
			position: "absolute",
			top: 85,
			left: 20,
		},
		concessionCardNumber: {
			fontFamily: "Arial",
			padding: theme.spacing(1, 0),
			fontSize: theme.spacing(2),
			border: "none",
			background: "transparent",
			fontWeight: "bold",
			width: 155,
			outline: "none",
			marginRight: theme.spacing(1),
		},

		expiryDetailsContainer: {
			position: "absolute",
			top: 15,
			fontFamily: "Arial",
			fontSize: theme.spacing(3),
			display: "flex",
			flexDirection: "column",
			right: theme.spacing(1),
		},

		expiryDetails: {
			fontFamily: "Arial",
			padding: theme.spacing(1, 0),
			fontSize: theme.spacing(2),
			border: "none",
			background: "transparent",
			width: "80px",
			fontWeight: "bold",
			textAlign: "center",
			outline: "none",
			margin: "auto",
		},
		validToText: {
			display: "block",
			textAlign: "center",
			alignItems: "center",
			fontSize: theme.spacing(2),
			fontWeight: "bold",
		},
		crnText: {
			fontFamily: "Arial",
			display: "flex",
			marginRight: theme.spacing(1),
			alignItems: "center",
			fontSize: theme.spacing(2),
			fontWeight: "bold",
		},
		crnLetter: {
			fontFamily: "Arial",
			padding: theme.spacing(1, 0),
			fontSize: theme.spacing(2),
			border: "none",
			background: "transparent",
			fontWeight: "bold",
			width: 20,
			textTransform: "uppercase",
			outline: "none",
		},
	});

export interface ConcessionCardProps
	extends React.HTMLAttributes<HTMLDivElement> {}

const ConcessionCard = React.forwardRef<
	HTMLDivElement,
	ConcessionCardProps & WithStyles<typeof styles>
>(({ classes, className, ...divProps }, ref) => {
	const {
		concessionCard,
		concessionCardError,
		handleConcessionCardErrorChange,
		handleConcessionCardChange,
	} = useApplicationManager();

	React.useEffect(() => {
		try {
			const expiry = AuditableDocument.formatExpiry(
				concessionCard.expireAt,
				10
			);
			if (Concession.expiryIsInvalid(expiry)) {
				throw new InvalidExpiryError();
			} else {
				handleConcessionCardErrorChange({
					...InitialConcessionCardErrorState,
				});
			}
		} catch (error) {
			handleConcessionCardErrorChange({
				...concessionCardError,
				expireAtError: error.message,
			});
		}

		const { error } = Concession.schema.validate(concessionCard);

		// console.log(Concession.schema.validate(concessionCard));

		if (error) {
			const { details } = error;

			let errorObj: IConcessionCardError = {
				...InitialConcessionCardErrorState,
			};

			for (let i = 0; i < details.length; i++) {
				const currentDetail = details[i];
				const key = currentDetail.path[0] + "Error";
				errorObj = { ...errorObj, [key]: currentDetail.message };
			}

			handleConcessionCardErrorChange(errorObj);
		}
	}, [concessionCard]);

	// Handlers
	const handleConcessionNumberBlur = (
		event: React.FocusEvent<HTMLInputElement>
	) => {
		const val = event.target.value;
		const sanitisedVal = Concession.sanitise(val);

		handleConcessionCardChange({
			...concessionCard,
			cardNumber: sanitisedVal,
		});
	};

	const handleConcessionLetterBlur = (
		event: React.FocusEvent<HTMLInputElement>
	) => {
		handleConcessionCardChange({
			...concessionCard,
			letter: event.target.value,
		});
	};

	const handleExpiryBlur = (event: React.FocusEvent<HTMLInputElement>) => {
		handleConcessionCardChange({
			...concessionCard,
			expireAt: event.target.value,
		});
	};

	return (
		<div className={clsx(classes.root, className)} ref={ref} {...divProps}>
			<div className={classes.imgContainer}>
				<img
					className={classes.img}
					src={BLANK_HEALTHCARE}
					alt="Blank concession card for patient interaction"
				/>
			</div>
			<div className={classes.concessionCardNumberWrapper}>
				<div className={classes.concessionCardNumberContainer}>
					<div className={classes.crnText}>
						<span>CRN</span>
					</div>
					<Tooltip
						title="Enter your concession card number"
						arrow
						placement="top"
					>
						<NumberFormat
							format="###-###-###"
							mask="_"
							className={classes.concessionCardNumber}
							placeholder="Concession number"
							autoFocus
							onBlur={handleConcessionNumberBlur}
						/>
					</Tooltip>
					<Tooltip
						title="Enter last letter of your concession number"
						arrow
						placement="top"
					>
						<input
							className={classes.crnLetter}
							placeholder="A"
							maxLength={1}
							onBlur={handleConcessionLetterBlur}
						/>
					</Tooltip>
				</div>
			</div>
			<div className={classes.expiryDetailsContainer}>
				<div className={classes.validToText}>Expires</div>
				<Tooltip title="Expiry date of your concession card.">
					<ThemeProvider theme={medicareTheme}>
						<NumberFormat
							format="##/##/####"
							mask="_"
							placeholder="Expiry"
							className={classes.expiryDetails}
							onBlur={handleExpiryBlur}
						/>
					</ThemeProvider>
				</Tooltip>
			</div>
		</div>
	);
});

const StyledConcessionCard = withStyles(styles, {
	classNamePrefix: Utility.generateClassName("ConcessionCard"),
})(ConcessionCard);

export default StyledConcessionCard;

export const AnimatedConcessionCard = animated(StyledConcessionCard);
